import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import UploadIcon from '@mui/icons-material/Upload';
import axiosInstance from '../services/axiosInstance';
import AdvancedSearchBar, { AdvancedSearchParams } from '../components/AdvancedSearchBar';
import AssetDetails from '../components/AssetDetails';
import EnhancedUploadModal from '../components/EnhancedUploadModal';
import { ICategory } from '../types/damTypes';
import ArchiveAssetCard, { ArchiveGridAsset } from './archive/ArchiveAssetCard';
import { categoryIdsUnderMain, listMainCategories } from './archive/archiveCardText';

const PAGE_SIZE = 300;

const getDefaultSearchParams = (): AdvancedSearchParams => ({
  searchTerm: '',
  fileTypes: [],
  fromDate: null,
  toDate: null,
  favorite: null,
  sortBy: 'uploadedAt',
  sortDirection: 'desc',
  searchFields: ['title', 'description', 'tags', 'aiTags', 'aiDescription', 'fileName', 'metadata'],
  recentSearches: [],
  searchOperator: 'OR'
});

const errorMessage = (error: unknown, fallback: string): string => {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err?.response?.data?.message || err?.message || fallback;
};

const buildArchiveQuery = (
  searchParams: AdvancedSearchParams,
  skip: number,
  categoryIds: string[],
): URLSearchParams => {
  const queryParams = new URLSearchParams();
  if (searchParams.searchTerm.trim()) {
    queryParams.append('search', searchParams.searchTerm);
    if (searchParams.searchOperator) queryParams.append('operator', searchParams.searchOperator);
  }
  if (searchParams.fileTypes.length > 0) queryParams.append('fileTypes', searchParams.fileTypes.join(','));
  if (searchParams.fromDate) queryParams.append('fromDate', searchParams.fromDate.toISOString());
  if (searchParams.toDate) queryParams.append('toDate', searchParams.toDate.toISOString());
  if (searchParams.favorite !== null) queryParams.append('favorite', searchParams.favorite.toString());
  if (searchParams.searchFields.length > 0) queryParams.append('searchFields', searchParams.searchFields.join(','));
  if (searchParams.sortBy) {
    queryParams.append('sortBy', searchParams.sortBy);
    queryParams.append('sortDirection', searchParams.sortDirection);
  }
  if (categoryIds.length > 0) queryParams.append('category', categoryIds.join(','));
  queryParams.append('archived', 'true');
  queryParams.append('limit', String(PAGE_SIZE));
  if (skip > 0) queryParams.append('skip', String(skip));
  return queryParams;
};

interface ArchivePageProps {
  onClose: () => void;
  canDownload?: boolean;
  canUpload?: boolean;
}

const ArchivePage: React.FC<ArchivePageProps> = ({
  onClose,
  canDownload = false,
  canUpload = false,
}) => {
  const [items, setItems] = useState<ArchiveGridAsset[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestGeneration = useRef(0);
  const [searchParams, setSearchParams] = useState<AdvancedSearchParams>(getDefaultSearchParams());
  const [reloadToken, setReloadToken] = useState(0);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedMainCategoryId, setSelectedMainCategoryId] = useState<string | null>(null);

  const applyPage = useCallback((
    nextItems: ArchiveGridAsset[],
    total: number | undefined,
    mode: 'replace' | 'append',
  ) => {
    setTotalCount(total);
    setItems(prev => {
      const combined = mode === 'append' ? [...prev, ...nextItems] : nextItems;
      if (mode === 'replace') {
        setViewingId(current => (
          current && !combined.some(item => item._id === current) ? null : current
        ));
      }
      return combined;
    });
    if (mode === 'replace') setSelectedIds(new Set());
  }, []);

  const mainCategories = useMemo(() => listMainCategories(categories), [categories]);
  const activeCategoryIds = useMemo(
    () => (selectedMainCategoryId ? categoryIdsUnderMain(categories, selectedMainCategoryId) : []),
    [categories, selectedMainCategoryId],
  );

  const fetchPage = useCallback(async (skip: number) => {
    const resp = await axiosInstance.get('/api/assets', {
      params: Object.fromEntries(buildArchiveQuery(searchParams, skip, activeCategoryIds)),
    });
    const headerTotal = Number(resp.headers?.['x-total-count']);
    return {
      items: (Array.isArray(resp.data) ? resp.data : []) as ArchiveGridAsset[],
      total: Number.isFinite(headerTotal) ? headerTotal : undefined,
    };
  }, [searchParams, activeCategoryIds]);

  useEffect(() => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setLoadError(null);
    fetchPage(0)
      .then(page => {
        if (generation !== requestGeneration.current) return;
        applyPage(page.items, page.total, 'replace');
      })
      .catch(error => {
        if (generation !== requestGeneration.current) return;
        setItems([]);
        setTotalCount(undefined);
        setLoadError(errorMessage(error, 'Kunde inte hämta arkivet.'));
      })
      .finally(() => {
        if (generation === requestGeneration.current) setLoading(false);
      });
  }, [fetchPage, applyPage, reloadToken]);

  useEffect(() => {
    let active = true;
    axiosInstance.get<ICategory[]>('/api/categories')
      .then(resp => {
        if (active) setCategories(Array.isArray(resp.data) ? resp.data : []);
      })
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSearch = useCallback((params: AdvancedSearchParams) => {
    setSearchParams(params);
  }, []);

  const loadMore = async () => {
    const generation = requestGeneration.current;
    setLoadingMore(true);
    setLoadError(null);
    try {
      const page = await fetchPage(items.length);
      if (generation !== requestGeneration.current) return;
      applyPage(page.items, page.total, 'append');
    } catch (error) {
      if (generation !== requestGeneration.current) return;
      setLoadError(errorMessage(error, 'Kunde inte hämta fler filer.'));
    } finally {
      if (generation === requestGeneration.current) setLoadingMore(false);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map(item => item._id)));
  };

  const restoreIds = async (ids: string[]) => {
    if (ids.length === 0) return;
    setIsProcessing(true);
    setActionError(null);
    try {
      await Promise.all(ids.map(id => axiosInstance.put(`/api/assets/${id}`, { archived: false })));
      setViewingId(current => (current && ids.includes(current) ? null : current));
      setReloadToken(token => token + 1);
    } catch (error) {
      setActionError(errorMessage(error, 'Kunde inte återställa.'));
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteIds = async (ids: string[]) => {
    if (ids.length === 0) return;
    setIsProcessing(true);
    setActionError(null);
    try {
      await Promise.all(ids.map(id => axiosInstance.delete(`/api/assets/${id}`)));
      setViewingId(current => (current && ids.includes(current) ? null : current));
      setReloadToken(token => token + 1);
    } catch (error) {
      setActionError(errorMessage(error, 'Kunde inte radera.'));
    } finally {
      setIsProcessing(false);
    }
  };

  const restore = (id: string) => {
    if (!window.confirm('Vill du återställa denna fil till biblioteket?')) return;
    void restoreIds([id]);
  };

  const restoreSelected = () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Vill du återställa ${selectedIds.size} filer?`)) return;
    void restoreIds(Array.from(selectedIds));
  };

  const remove = (id: string) => {
    if (!window.confirm('Vill du radera denna fil permanent? Detta går inte att ångra.')) return;
    void deleteIds([id]);
  };

  const removeSelected = () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Vill du radera ${selectedIds.size} filer permanent? Detta går inte att ångra.`)) return;
    void deleteIds(Array.from(selectedIds));
  };

  const hasMore = typeof totalCount === 'number' && items.length < totalCount;
  const countLabel = typeof totalCount === 'number' && totalCount !== items.length
    ? `${items.length} av ${totalCount}`
    : String(items.length);

  return (
    <Box sx={{ p: 2, position: 'relative' }}>
      <IconButton aria-label="Stäng" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, pr: 5 }}>
        <Typography variant="h5">Arkiv</Typography>
        {canUpload && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setUploadOpen(true)}
          >
            Ladda upp till arkiv
          </Button>
        )}
      </Box>

      {mainCategories.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant={selectedMainCategoryId ? 'outlined' : 'contained'}
            onClick={() => setSelectedMainCategoryId(null)}
            sx={selectedMainCategoryId ? undefined : { bgcolor: '#072f53', '&:hover': { bgcolor: '#051a2e' } }}
          >
            Alla
          </Button>
          {mainCategories.map(category => {
            const selected = selectedMainCategoryId === category._id;
            return (
              <Button
                key={category._id}
                size="small"
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => setSelectedMainCategoryId(current => current === category._id ? null : category._id)}
                sx={selected ? { bgcolor: '#072f53', '&:hover': { bgcolor: '#051a2e' } } : undefined}
              >
                {category.name}
              </Button>
            );
          })}
        </Box>
      )}

      <Box sx={{ mb: 2 }}>
        <AdvancedSearchBar onSearch={handleSearch} initialParams={searchParams} />
      </Box>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      {items.length > 0 && (
        <Paper
          elevation={0}
          sx={{ mb: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={selectedIds.size === items.length && items.length > 0}
              indeterminate={selectedIds.size > 0 && selectedIds.size < items.length}
              onChange={selectAll}
              disabled={isProcessing}
            />
            <Typography variant="body2" sx={{ ml: 1 }}>
              {selectedIds.size > 0
                ? `${selectedIds.size} av ${countLabel} markerade`
                : `Markera alla (${countLabel})`}
            </Typography>
          </Box>
          {selectedIds.size > 0 && (
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button size="small" variant="contained" startIcon={<RestoreIcon />} onClick={restoreSelected} disabled={isProcessing}>
                Återställ ({selectedIds.size})
              </Button>
              <Button size="small" variant="contained" color="error" startIcon={<DeleteIcon />} onClick={removeSelected} disabled={isProcessing}>
                Radera ({selectedIds.size})
              </Button>
            </Box>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading && items.length === 0 && !loadError && (
            <Typography variant="body2" color="text.secondary">Hämtar arkivet...</Typography>
          )}
          {!loading && items.length === 0 && !loadError && (
            <Typography variant="body2" color="text.secondary">Inga arkiverade filer.</Typography>
          )}
          <Box sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: 'repeat(auto-fill, minmax(180px, 1fr))',
              sm: 'repeat(auto-fill, minmax(200px, 1fr))',
              md: 'repeat(auto-fill, minmax(220px, 1fr))',
            },
          }}>
            {items.map(item => (
              <ArchiveAssetCard
                key={item._id}
                asset={item}
                categories={categories}
                selected={selectedIds.has(item._id)}
                viewing={viewingId === item._id}
                disabled={isProcessing}
                onOpen={() => setViewingId(item._id)}
                onToggleSelect={() => toggleSelect(item._id)}
                onRestore={() => restore(item._id)}
                onDelete={() => remove(item._id)}
              />
            ))}
          </Box>
          {hasMore && (
            <Button sx={{ mt: 2 }} onClick={loadMore} disabled={loadingMore || isProcessing}>
              {loadingMore ? 'Hämtar...' : 'Visa fler'}
            </Button>
          )}
          <Button sx={{ mt: 2, ml: hasMore ? 1 : 0 }} onClick={onClose}>Stäng</Button>
        </Box>

        {viewingId && (
          <Box sx={{
            width: { xs: '100%', md: 420 },
            flexShrink: 0,
            height: '80vh',
            position: 'sticky',
            top: 8,
            bgcolor: '#f5f5f5',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <IconButton
              aria-label="Stäng information"
              onClick={() => setViewingId(null)}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, bgcolor: 'white' }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ pt: 1, px: 1, height: '100%', overflow: 'hidden' }}>
              <AssetDetails
                assetId={viewingId}
                allCategories={categories}
                onAssetDeleted={() => setViewingId(null)}
                readOnly
                canDownload={canDownload}
                layoutVariant="panel"
              />
            </Box>
          </Box>
        )}
      </Box>

      {canUpload && (
        <EnhancedUploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onUploadSuccess={() => setReloadToken(token => token + 1)}
          categories={categories}
          destination="archive"
        />
      )}
    </Box>
  );
};

export default ArchivePage;
